import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../lib/api';
import { CampaignFunnel, CampaignsOverview } from '../../types/accounts';
import { Spinner } from '../ui';

type Channel = 'email' | 'linkedin';
const pct = (n = 0, d = 0) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—');
const num = (f: CampaignFunnel, k: string) => f.latest?.[k] ?? 0;

const EMAIL_COLS: { key: string; label: string; sortable: boolean; val: (f: CampaignFunnel) => number | string; sort: (f: CampaignFunnel) => number }[] = [
  { key: 'client', label: 'Client', sortable: false, val: (f) => f.entity_name ?? '—', sort: () => 0 },
  { key: 'name', label: 'Campaign', sortable: false, val: (f) => f.name ?? f.id, sort: () => 0 },
  { key: 'sent', label: 'Sent', sortable: true, val: (f) => num(f, 'emails_sent_count'), sort: (f) => num(f, 'emails_sent_count') },
  { key: 'open', label: 'Open%', sortable: true, val: (f) => pct(num(f, 'open_count_unique'), num(f, 'emails_sent_count')), sort: (f) => num(f, 'open_count_unique') / (num(f, 'emails_sent_count') || 1) },
  { key: 'reply', label: 'Reply', sortable: true, val: (f) => num(f, 'reply_count_unique'), sort: (f) => num(f, 'reply_count_unique') },
  { key: 'opp', label: 'Opp', sortable: true, val: (f) => num(f, 'total_opportunities'), sort: (f) => num(f, 'total_opportunities') },
  { key: 'mtg', label: 'Mtg', sortable: true, val: (f) => num(f, 'total_meeting_booked'), sort: (f) => num(f, 'total_meeting_booked') },
  { key: '24h', label: '24h Sent', sortable: true, val: (f) => f.delta_24h?.emails_sent_count ?? 0, sort: (f) => f.delta_24h?.emails_sent_count ?? 0 },
];

const LI_COLS: typeof EMAIL_COLS = [
  { key: 'client', label: 'Client', sortable: false, val: (f) => f.entity_name ?? '—', sort: () => 0 },
  { key: 'name', label: 'Account', sortable: false, val: (f) => f.name ?? f.id, sort: () => 0 },
  { key: 'inv', label: 'Inv', sortable: true, val: (f) => num(f, 'invited'), sort: (f) => num(f, 'invited') },
  { key: 'acc', label: 'Acc', sortable: true, val: (f) => num(f, 'accepted'), sort: (f) => num(f, 'accepted') },
  { key: 'repl', label: 'Repl', sortable: true, val: (f) => num(f, 'replied'), sort: (f) => num(f, 'replied') },
  { key: 'msg', label: 'Msg 1/2/3', sortable: true, val: (f) => `${num(f, 'msg1')}/${num(f, 'msg2')}/${num(f, 'msg3')}`, sort: (f) => num(f, 'msg1') },
  { key: '24h', label: '24h Inv', sortable: true, val: (f) => f.delta_24h?.invited ?? 0, sort: (f) => f.delta_24h?.invited ?? 0 },
];

export function CampaignsView({ onOpenClient }: { onOpenClient: (entityId: string) => void }) {
  const [data, setData] = useState<CampaignsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<Channel>('email');
  const [sortKey, setSortKey] = useState('sent');

  useEffect(() => {
    apiClient.getAdminCampaigns().then(setData)
      .catch(() => setError('Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, []);

  const cols = channel === 'email' ? EMAIL_COLS : LI_COLS;
  const rows = useMemo(() => {
    const list = (channel === 'email' ? data?.email : data?.linkedin) ?? [];
    const col = cols.find((c) => c.key === sortKey);
    if (!col) return list;
    return [...list].sort((a, b) => col.sort(b) - col.sort(a));
  }, [data, channel, sortKey, cols]);

  if (loading) return <div className="p-6"><Spinner size="md" center text="Loading..." /></div>;
  if (error) return <div className="p-6"><p className="text-sm text-error-600">{error}</p></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex gap-2 mb-4">
        {(['email', 'linkedin'] as Channel[]).map((c) => (
          <button key={c} onClick={() => { setChannel(c); setSortKey(c === 'email' ? 'sent' : 'inv'); }}
            className={`px-3 py-1.5 rounded text-sm font-medium ${channel === c ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
            {c === 'email' ? 'Email' : 'LinkedIn'}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto border border-neutral-200 rounded-md bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {cols.map((c) => (
                <th key={c.key} onClick={() => c.sortable && setSortKey(c.key)}
                  className={`text-left px-3 py-2 font-semibold text-neutral-600 select-none whitespace-nowrap ${c.sortable ? 'cursor-pointer' : ''}`}>
                  {c.label}{c.sortable && sortKey === c.key ? ' ↓' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} onClick={() => f.entity_id && onOpenClient(f.entity_id)}
                className="border-b border-neutral-100 hover:bg-primary-50/50 cursor-pointer">
                {cols.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-neutral-900 whitespace-nowrap">{c.val(f)}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-neutral-400">No campaigns synced yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
